import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowLeft, ArrowRight, Check, ImagePlus, Trash, Sparkles, Download, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GENERATE_AUTH_TOKEN } from '../config';
import LoadingAnimation from '../components/LoadingAnimation';

type Step = 'intro' | 'brand' | 'audience' | 'logo' | 'success';

interface FormData {
  brandDescription: string;
  targetAudience: string;
}


export const BrandDescription = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<Step>('intro');
  const [formData, setFormData] = useState<FormData>({
    brandDescription: '',
    targetAudience: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Logo step state
  const [hasLogo, setHasLogo] = useState<boolean | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [generatingLogo, setGeneratingLogo] = useState(false);
  const [generatedLogoUrl, setGeneratedLogoUrl] = useState<string | null>(null);
  // No blocking analysis state; analysis generation runs in background

  

  // Load existing data if available
  // Track if component is mounted to prevent state updates after navigation
  const [isMounted, setIsMounted] = useState(true);
  const genCreateTriggeredRef = useRef(false);

  useEffect(() => {
    // Set mounted state
    setIsMounted(true);
    
    // Cleanup function to set mounted state to false when component unmounts
    return () => {
      setIsMounted(false);
    };
  }, []);

  // Trigger analysis generation without blocking navigation and persist result when ready
  // When forceBypass is true, skip the lock mechanism (used when completing the brand flow with a logo)
  const triggerMarketingAnalysisInBackground = async (logoUrl?: string, forceBypass = false) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Resolve id_company to update config later
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();
      const companyId = (profile as any)?.id_company as string | undefined;

      // Use same auth as image generation webhooks
      const headers: Record<string, string> = { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN };

      // Fire the webhook request (do not await for navigation purposes)
      let shouldTrigger = forceBypass; // Bypass lock if explicitly requested
      if (!forceBypass) {
        try {
          const LOCK_KEY = 'atlas:identity:gen:lock';
          const LOCK_TTL_MS = 15 * 60 * 1000;
          if (!genCreateTriggeredRef.current) {
            const lastStr = localStorage.getItem(LOCK_KEY);
            const last = lastStr ? parseInt(lastStr, 10) : 0;
            const now = Date.now();
            shouldTrigger = !last || (now - last > LOCK_TTL_MS);
            if (shouldTrigger) {
              localStorage.setItem(LOCK_KEY, String(now));
              genCreateTriggeredRef.current = true;
            }
          }
        } catch (_e) {
          if (!genCreateTriggeredRef.current) {
            genCreateTriggeredRef.current = true;
            shouldTrigger = true;
          }
        }
      }

      if (!shouldTrigger) return;

      const requestBody: { user_id: string; mode: string; logo_url?: string } = { 
        user_id: user.id, 
        mode: 'create' 
      };
      if (logoUrl) {
        requestBody.logo_url = logoUrl;
      }

      fetch('https://ai-agency-dt7d.onrender.com/webhook/generar_identidad', {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      })
        .then(async (response) => {
          if (!response.ok) return;
          let data: any = null;
          try {
            data = await response.json();
          } catch (_e) {
            // ignore json parse errors
          }
          if (!companyId) return;
          const analysis = data?.response || '';
          try {
            await supabase
              .from('config')
              .update({ media_agent_analysis: analysis })
              .eq('id_company', companyId);
          } catch (e) {
            console.error('Error saving background marketing analysis:', e);
          }
        })
        .catch((err) => {
          console.error('Background marketing analysis generation failed:', err);
        });
    } catch (e) {
      console.error('Error preparing background marketing analysis:', e);
    }
  };

  useEffect(() => {
    const fetchExistingData = async () => {
      try {
        if (!isMounted) return;
        
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !isMounted) return;

        // Get user's id_company
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();

        if (!(profile as any)?.id_company || !isMounted) return;
        
        // Get existing config data
        const { data: configData } = await supabase
          .from('config')
          .select('media_agent_user_description, media_agent_style, media_agent_public, media_agent_objective, media_agent_analysis')
          .eq('id_company', (profile as any).id_company)
          .maybeSingle();
        
        if (configData && isMounted) {
          setFormData({
            brandDescription: configData.media_agent_user_description || '',
            targetAudience: configData.media_agent_public || '',
          });
          
          // Do not auto-redirect or auto-advance steps. Intro remains as default.
        }
      } catch (err) {
        console.error('Error fetching existing data:', err);
        if (isMounted) {
          setError('Error al cargar los datos existentes');
        }
      }
    };

    fetchExistingData();
  }, [isMounted, navigate]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 'intro':
        // No validation needed in the intro screen
        break;
      case 'brand':
        if (!formData.brandDescription.trim()) {
          setError('Por favor, ingresa una descripción de tu marca');
          return false;
        }
        break;
      case 'audience':
        if (!formData.targetAudience.trim()) {
          setError('Por favor, describe tu público objetivo');
          return false;
        }
        break;
    }
    return true;
  };

  const saveCurrentStep = async (): Promise<boolean> => {
    // Intro step does not persist anything
    if (currentStep === 'intro') return true;
    try {
      setLoading(true);
      setError(null);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Get user's id_company
      const { data: profile } = await supabase
        .from('profiles')
        .select('id_company')
        .eq('id', user.id)
        .single();

      if (!(profile as any)?.id_company) throw new Error('No company associated with user');
      
      // Determine which field to update based on current step
      let updateData = {} as Record<string, any>;
      switch (currentStep) {
        case 'brand':
          updateData = { media_agent_user_description: formData.brandDescription };
          break;
        case 'audience':
          updateData = { media_agent_public: formData.targetAudience };
          break;
      }
      
      // Update the config table
      const { error: updateError } = await supabase
        .from('config')
        .update(updateData)
        .eq('id_company', (profile as any).id_company);
      
      if (updateError) throw updateError;
      
      return true;
    } catch (err) {
      console.error(`Error saving ${currentStep}:`, err);
      setError(err instanceof Error ? err.message : `Error al guardar ${getStepName(currentStep)}`);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // generateMarketingAnalysis function removed in favor of background trigger without blocking UI

  // Marketing plan approval is now handled in the MarketingPlanReview component

  // Marketing analysis is now saved directly in the generateMarketingAnalysis function

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    const file = e.target.files[0];
    
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona un archivo de imagen válido');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('El archivo es demasiado grande. El tamaño máximo es 10MB');
      return;
    }
    
    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
    setError(null);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreviewUrl(null);
    setGeneratedLogoUrl(null);
  };

  const handleGenerateLogo = async () => {
    try {
      setGeneratingLogo(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const response = await fetch('https://ai-agency-dt7d.onrender.com/webhook/crear_logo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': GENERATE_AUTH_TOKEN,
        },
        body: JSON.stringify({ user_id: user.id }),
      });

      if (!response.ok) {
        throw new Error('Error al generar el logo');
      }

      const data = await response.json();
      
      if (data.logo_url) {
        setGeneratedLogoUrl(data.logo_url);
        setLogoPreviewUrl(data.logo_url);
      } else {
        throw new Error('No se recibió la URL del logo');
      }
    } catch (err) {
      console.error('Error generating logo:', err);
      setError(err instanceof Error ? err.message : 'Error al generar el logo');
    } finally {
      setGeneratingLogo(false);
    }
  };

  const saveLogoToStorage = async (): Promise<string | null> => {
    if (!logoFile) return null; // No logo to save
    
    try {
      setUploadingLogo(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Upload logo to storage in logos folder
      const timestamp = Date.now();
      const fileExt = logoFile.name.split('.').pop();
      const fileName = `logos/${timestamp}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('system')
        .upload(fileName, logoFile);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('system')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (err) {
      console.error('Error saving logo:', err);
      setError(err instanceof Error ? err.message : 'Error al guardar el logo');
      return null;
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleNext = async () => {
    if (!validateCurrentStep()) return;
    
    const saved = await saveCurrentStep();
    if (!saved) return;
    
    // Move to next step
    switch (currentStep) {
      case 'intro':
        setCurrentStep('brand');
        break;
      case 'brand':
        setCurrentStep('audience');
        break;
      case 'audience':
        setCurrentStep('logo');
        break;
      case 'logo':
        // Upload logo if provided and get URL, or use generated logo URL
        let finalLogoUrl: string | undefined;
        
        if (logoFile) {
          // User uploaded a logo file
          const uploadedLogoUrl = await saveLogoToStorage();
          if (!uploadedLogoUrl) return; // Upload failed
          finalLogoUrl = uploadedLogoUrl;
        } else if (generatedLogoUrl) {
          // User generated a logo with AI
          finalLogoUrl = generatedLogoUrl;
        }
        
        // Trigger analysis in background with logo URL, bypassing lock since user explicitly completed the flow
        triggerMarketingAnalysisInBackground(finalLogoUrl, true);
        setCurrentStep('success');
        break;
      case 'success':
        navigate('/redes-sociales/crear-anuncio/seleccionar-productos', { replace: true, state: { hideBrandBanner: true } });
        break;
    }
  };

  const handleBack = () => {
    switch (currentStep) {
      case 'brand':
        setCurrentStep('intro');
        break;
      case 'audience':
        setCurrentStep('brand');
        break;
      case 'logo':
        // If user selected Si or No, go back to selection
        if (hasLogo !== null) {
          setHasLogo(null);
          setLogoFile(null);
          setLogoPreviewUrl(null);
        } else {
          setCurrentStep('audience');
        }
        break;
    }
    setError(null);
  };

  const getStepName = (step: Step): string => {
    switch (step) {
      case 'intro': return 'Introducción';
      case 'brand': return 'Descripción de marca';
      case 'audience': return 'Público objetivo';
      case 'logo': return 'Logo';
      case 'success': return 'Éxito';
    }
  };


  // Titles and descriptions to show OUTSIDE the white container
  const stepTexts = (() => {
    switch (currentStep) {
      case 'intro':
        return { title: '¡Vamos a conocernos!', subtitle: 'Hola, soy la IA de Atlas, el asistente virtual de marketing que te va a ayudar a que tu marca alcance el siguiente nivel.' };
      case 'brand':
        return { title: 'Contame un poco sobre tu marca', subtitle: 'Describimela brevemente y decime qué es lo que la hace diferente.' };
      case 'audience':
        return { title: '¿A quién querés llegar?', subtitle: 'Contame a qué tipo de personas apuntás: edades, intereses y gustos.' };
      case 'logo':
        if (hasLogo === false) {
          return { title: '¿Te gustaría tener un logo profesional?', subtitle: '' };
        } else if (hasLogo === true) {
          return { title: 'Subí tu logo', subtitle: 'Si subís una foto de tu logo, podes añadirlo a las fotos que generes.' };
        }
        return { title: '¿Tu marca tiene un logo?', subtitle: '' };
      case 'success':
        return { title: 'Fotos mejoradas', subtitle: 'Ahora la IA entenderá tu marca y hará fotos más acorde a tu estilo.' };
    }
  })();

  const renderStepContent = () => {
    switch (currentStep) {
      case 'intro':
        return (
          <div className="space-y-3 text-center">
            <p className="text-[15px] text-brand-black/60">
            ¿Me contarías un poco sobre tu marca? Necesito conocerla para poder crear anuncios personalizados, que se sientan propios de tu marca.
            </p>
          </div>
        );
      case 'brand':
        return (
          <div className="space-y-4">
            <textarea
              value={formData.brandDescription}
              onChange={(e) => handleInputChange('brandDescription', e.target.value)}
              placeholder="Describe tu marca, productos, servicios, valores..."
              className="w-full p-3 rounded-md bg-white border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary resize-none min-h-[150px] text-[15px] text-brand-black placeholder:text-neutral-400"
              rows={6}
            />
          </div>
        );
      case 'audience':
        return (
          <div className="space-y-4">
            <textarea
              value={formData.targetAudience}
              onChange={(e) => handleInputChange('targetAudience', e.target.value)}
              placeholder="Describe a quién va dirigido tu producto o servicio, edad, intereses, comportamientos..."
              className="w-full p-3 rounded-md bg-white border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary resize-none min-h-[150px] text-[15px] text-brand-black placeholder:text-neutral-400"
              rows={6}
            />
          </div>
        );
      case 'logo':
        return (
          <div className="space-y-4">
            {/* Si/No buttons */}
            {hasLogo === null && (
              <div className="flex flex-col gap-3 items-center pt-6">
                <button
                  type="button"
                  onClick={() => setHasLogo(true)}
                  className="w-full max-w-[200px] h-10 rounded-md border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium hover:bg-neutral-50 transition-colors shadow-sm"
                >
                  Sí
                </button>
                <button
                  type="button"
                  onClick={() => setHasLogo(false)}
                  className="w-full max-w-[200px] h-10 rounded-md border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium hover:bg-neutral-50 transition-colors shadow-sm"
                >
                  No
                </button>
              </div>
            )}

            {/* Has logo - show upload */}
            {hasLogo === true && (
              <div className="space-y-4">
                {logoPreviewUrl ? (
                  <div className="flex flex-col items-center gap-3 pt-4">
                    <div className="relative w-48 h-48 rounded-xl overflow-hidden border border-gray-200 bg-white">
                      <img 
                        src={logoPreviewUrl} 
                        alt="Logo preview" 
                        className="w-full h-full object-contain p-2"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
                      >
                        <Trash className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                    <p className="text-[13px] text-brand-black/50">Logo cargado correctamente</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3 pt-6">
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      disabled={uploadingLogo}
                    />
                    <label
                      htmlFor="logo-upload"
                      className={`inline-flex items-center justify-center gap-2 h-10 w-full max-w-[200px] bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md cursor-pointer transition-colors shadow-sm ${
                        uploadingLogo ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Subiendo...
                        </>
                      ) : (
                        <>
                          <ImagePlus className="w-5 h-5" />
                          Subir logo
                        </>
                      )}
                    </label>
                  </div>
                )}
              </div>
            )}

            {/* No logo - show generate option or generated logo preview */}
            {hasLogo === false && (
              <div className="flex flex-col items-center pt-6 gap-4">
                {generatedLogoUrl ? (
                  <div className="flex flex-col items-center gap-4">
                    <div className="relative w-48 h-48 rounded-xl overflow-hidden border border-gray-200 bg-white">
                      <img
                        src={generatedLogoUrl}
                        alt="Logo generado"
                        className="w-full h-full object-contain p-2"
                      />
                    </div>
                    <div className="flex items-center gap-3">
                      <a
                        href={generatedLogoUrl}
                        download="logo.png"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 h-10 px-4 bg-surface-card hover:bg-neutral-100 text-neutral-600 text-[15px] font-medium border border-neutral-200 rounded-md transition-colors shadow-sm"
                      >
                        <Download className="w-4 h-4" />
                        Descargar
                      </a>
                      <button
                        type="button"
                        onClick={handleGenerateLogo}
                        disabled={generatingLogo}
                        className="inline-flex items-center gap-2 h-10 px-4 bg-surface-card hover:bg-neutral-100 text-neutral-600 text-[15px] font-medium border border-neutral-200 rounded-md transition-colors shadow-sm disabled:opacity-50"
                      >
                        {generatingLogo ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4" />
                        )}
                        Regenerar
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleGenerateLogo}
                    disabled={generatingLogo}
                    className={`inline-flex items-center gap-2 h-10 px-6 bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md transition-colors shadow-sm ${
                      generatingLogo ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {generatingLogo ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Generar logo con IA
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        );
      case 'success':
        return (
          <div className="flex flex-col items-center justify-center py-4 space-y-4">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="w-10 h-10 text-green-500" />
            </div>
          </div>
        );
    }
  };

  // Success screen removed; we navigate to Home immediately after final step

  // Marketing plan review has been moved to MarketingPlanReview component

  const introIllustration = currentStep === 'intro' ? (
    <img
      src="https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/ilustracion-saludo.png"
      alt="Ilustración de una mujer saludando"
      className="w-full max-w-[240px] sm:max-w-[280px] mx-auto"
      loading="lazy"
    />
  ) : null;

  const isSimpleStep = currentStep === 'intro';
  const isTransparentStep = currentStep === 'logo' || currentStep === 'success';

  return (
    <>
      {/* Loading Animation for logo generation - 35 seconds */}
      {generatingLogo && (
        <LoadingAnimation
          message="Generando tu logo..."
          subMessage="Esto puede tomar unos segundos"
          durationMs={35000}
        />
      )}
      
      <div className="sm:p-8 min-h-[100svh] sm:min-h-screen w-full bg-[#EFF0F5] sm:flex sm:items-center sm:justify-center">
      <div className="w-full sm:w-[720px] mx-auto">
        {/* Progress Bar (desktop only here) */}
        {/*
        <div className="hidden sm:block mt-4 mb-8">
          <div className="w-full h-1 bg-gray-200 rounded-full">
            <div className="h-1 bg-blue-500 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>
        */}

        {/* Mobile layout: top third header, bottom two-thirds content */}
        <div className="sm:hidden">
          <div className="h-[32svh] px-4 flex flex-col items-center justify-center text-center">
            <div>
              <h2 className="text-[17px] font-semibold text-brand-black/90">{stepTexts.title}</h2>
              {stepTexts.subtitle ? (
                <p className="text-[15px] text-brand-black/50 mt-1">{stepTexts.subtitle}</p>
              ) : null}
            </div>
            {/*
            <div className="w-full max-w-[720px] mt-3">
              <div className="w-full h-1 bg-gray-200 rounded-full">
                <div className="h-1 bg-blue-500 rounded-full transition-all" style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
            */}
          </div>
          
          {introIllustration && (
            <div className="px-4 mb-4 flex justify-center">
              {introIllustration}
            </div>
          )}
          <div className={`px-4 ${isSimpleStep ? 'pb-6' : 'h-[68svh]'}`}>
            <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} className={isSimpleStep ? '' : 'h-full'}>
              <div className={`rounded-2xl p-4 pb-4 mb-6 flex flex-col ${isTransparentStep ? '' : 'bg-white'} ${isSimpleStep ? '' : 'h-[calc(100%-1.5rem)]'}`}>
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-[13px] flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <p>{error}</p>
                  </div>
                )}
                <div className={isSimpleStep ? '' : 'flex-1 overflow-auto'}>
                  {renderStepContent()}
                </div>
                <div className="flex items-center justify-between pt-3">
                  {currentStep !== 'success' && (
                    <button 
                      type="button"
                      onClick={currentStep === 'intro' ? () => navigate(-1) : handleBack}
                      disabled={loading || uploadingLogo}
                      className="px-4 py-2 text-neutral-600 text-[15px] font-medium hover:bg-neutral-100 rounded-md transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      <span>Atrás</span>
                    </button>
                  )}
                  {currentStep === 'success' && <div />}
                  <button 
                    type="submit"
                    disabled={loading || uploadingLogo}
                    className={`inline-flex items-center gap-2 px-4 h-10 rounded-md shadow-sm text-[15px] font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                      currentStep === 'logo' && !logoFile && !generatedLogoUrl 
                        ? 'text-neutral-600 hover:bg-neutral-100' 
                        : 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                    }`}
                  >
                    {loading || uploadingLogo ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>{uploadingLogo ? 'Subiendo...' : 'Guardando...'}</span>
                      </>
                    ) : (
                      <>
                        <span>{
                          currentStep === 'success' ? 'Terminar' :
                          currentStep === 'logo' ? ((logoFile || generatedLogoUrl) ? 'Siguiente' : 'Saltear paso') :
                          currentStep === 'intro' ? 'Comenzar' : 'Siguiente'
                        }</span>
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Desktop layout */}
        <div className="hidden sm:block w-full sm:w-[720px] mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-[17px] font-semibold text-brand-black/90">{stepTexts.title}</h2>
            {stepTexts.subtitle ? (
              <p className="text-[15px] text-brand-black/50 mt-1">{stepTexts.subtitle}</p>
            ) : null}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); handleNext(); }} className="space-y-6">
            {introIllustration && (
              <div className="flex justify-center">
                {introIllustration}
              </div>
            )}
            <div className={`rounded-2xl p-6 ${isTransparentStep ? '' : 'bg-white'}`}>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-[13px] flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <p>{error}</p>
                </div>
              )}
              {renderStepContent()}
              <div className="flex items-center justify-between pt-6">
                {currentStep !== 'success' && (
                  <button 
                    type="button"
                    onClick={currentStep === 'intro' ? () => navigate(-1) : handleBack}
                    disabled={loading || uploadingLogo}
                    className="px-4 py-2 text-neutral-600 text-[15px] font-medium hover:bg-neutral-100 rounded-md transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    <span>Atrás</span>
                  </button>
                )}
                {currentStep === 'success' && <div />}
                <button 
                  type="submit"
                  disabled={loading || uploadingLogo}
                  className={`inline-flex items-center gap-2 px-4 h-10 rounded-md shadow-sm text-[15px] font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                    currentStep === 'logo' && !logoFile && !generatedLogoUrl 
                      ? 'text-neutral-600 hover:bg-neutral-100' 
                      : 'bg-brand-primary hover:bg-brand-primary-hover text-white'
                  }`}
                >
                  {loading || uploadingLogo ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{uploadingLogo ? 'Subiendo...' : 'Guardando...'}</span>
                    </>
                  ) : (
                    <>
                      <span>{
                        currentStep === 'success' ? 'Terminar' :
                        currentStep === 'logo' ? ((logoFile || generatedLogoUrl) ? 'Siguiente' : 'Saltear paso') :
                        currentStep === 'intro' ? 'Comenzar' : 'Siguiente'
                      }</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  );
};

export default BrandDescription;